import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Search, MapPin, ArrowUpRight, Star } from "lucide-react";
import PlanBadge from "../components/PlanBadge";

export default function JobsList() {
  const [jobs, setJobs] = useState([]);
  const [q, setQ] = useState("");
  const [location, setLocation] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (location) params.location = location;
      const { data } = await api.get("/jobs", { params });
      setJobs(data);
    } finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12 lg:py-16">
      <div className="label-eyebrow">Ocean Ash Ltd — Open roles</div>
      <h1 className="font-display font-black text-5xl lg:text-6xl mt-3 tracking-tight">
        Find your next chapter.
      </h1>

      <div className="mt-10 flex flex-col md:flex-row gap-3 max-w-3xl">
        <div className="flex items-center border border-[#0F0F0F] px-3 flex-1">
          <Search className="w-4 h-4 text-[#5C5C5C]" />
          <input
            data-testid="jobs-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Search title, keywords…"
            className="bg-transparent px-3 py-3 w-full focus:outline-none"
          />
        </div>
        <div className="flex items-center border border-[#0F0F0F] px-3 md:w-72">
          <MapPin className="w-4 h-4 text-[#5C5C5C]" />
          <input
            data-testid="jobs-location"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Location"
            className="bg-transparent px-3 py-3 w-full focus:outline-none"
          />
        </div>
        <button data-testid="jobs-search-btn" onClick={load} className="btn-ink">Search</button>
      </div>

      <div className="mt-12 divide-y divide-[#E2E0D8] border-t border-b border-[#E2E0D8]">
        {loading && <div className="py-10 text-[#5C5C5C]">Loading…</div>}
        {!loading && jobs.length === 0 && (
          <div data-testid="no-jobs" className="py-16 text-[#5C5C5C]">No roles match your filters.</div>
        )}
        {jobs.map((j) => (
          <Link key={j.job_id} to={`/jobs/${j.job_id}`} data-testid={`job-row-${j.job_id}`}
            className="flex items-center justify-between py-6 group hover:bg-[#F3F2EB] -mx-4 px-4 transition">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="label-eyebrow">{j.company}</div>
                {j.tier === "premium" && <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold bg-[#0F0F0F] text-[#FDFCF8] px-1.5 py-0.5"><Star className="w-2.5 h-2.5 text-[var(--brand-primary)]" /> Spotlight</span>}
                {j.tier === "featured" && <span className="text-[10px] uppercase tracking-widest font-semibold border border-[var(--brand-primary)] text-[var(--brand-primary)] px-1.5 py-0.5">Featured</span>}
                <PlanBadge plan={j.recruiter_plan} />
              </div>
              <h3 className="font-display font-bold text-2xl mt-1 group-hover:text-[var(--brand-primary)]">{j.title}</h3>
              <div className="text-sm text-[#5C5C5C] mt-1">{j.location} · {j.employment_type} {j.department ? `· ${j.department}` : ""} {j.is_volunteer ? "· Volunteer" : (j.salary_range ? `· ${j.salary_range}` : "")}</div>
              {j.org_type === "game" && (
                <div data-testid={`job-game-disclaimer-${j.job_id}`} className="text-[11px] mt-1.5 inline-flex items-center gap-1 font-semibold text-purple-800 bg-purple-100 px-1.5 py-0.5">
                  Game group · Not affiliated with any official organisation
                </div>
              )}
            </div>
            <ArrowUpRight className="w-6 h-6 ml-4 flex-shrink-0" />
          </Link>
        ))}
      </div>
    </div>
  );
}
